<template>
    <v-app :class="dynamicAppClasses">
        <v-overlay v-if="!isRouterReady" persistent class="align-center justify-center">
            <v-progress-circular indeterminate size="64" color="primary" />
        </v-overlay>
        <MessageModal
            v-if="showMessageModal && isReleaseNote"
            :show="showMessageModal"
            :message="getReleaseMessage"
            :title="getReleaseTitle"
            :isReleaseNote="isReleaseNote"
            @close="handleNoteClose"
        />
        <LockQueueModal />
        <Navbar v-if="isMainLayoutActive" @openReleaseNotes="openReleaseNotes" />
        <v-main class="main-content-area">
            <v-row v-if="currentTeamName" noGutters>
                <v-col>
                    <Breadcrumbs />
                </v-col>
            </v-row>
            <router-view :key="route.fullPath" />
        </v-main>
        <AQChatbot v-if="showChatbot" />
    </v-app>
</template>

<script setup lang="ts">
    import { onMounted, watch, computed, ref } from 'vue';
    import { useStore } from 'vuex';
    import { useRoute, useRouter } from 'vue-router';
    import 'vue3-toastify/dist/index.css';
    import { useLayout } from '@/composables/useLayout';
    import Breadcrumbs from '@/components/navigation/Breadcrumbs.vue';
    import LockQueueModal from '@/components/shared/modals/LockQueueModal.vue';
    import AQChatbot from '@/components/shared/chatbot/AQChatbot.vue';
    import MessageModal from '@/components/shared/modals/MessageModal.vue';
    import { WEB } from '@/constants/platforms';
    import $rq from './api/api';
    import { ReleaseNoteData, DisplayNoteData } from '@/interfaces/common';
    import { useI18n } from 'vue-i18n';

    const currentTeamName = computed<string | null>(() => store.getters['currentTeamName'] || null);

    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const isRouterReady = ref(false);
    router.isReady().then(() => {
        isRouterReady.value = true;
    });

    const { dynamicAppClasses, isMainLayoutActive } = useLayout();
    const release = ref<ReleaseNoteData>();
    const showMessageModal = ref(false);
    const isReleaseNote = ref(false);

    const showChatbot = computed<boolean>(() => {
        return (
            store.getters.isLoggedIn &&
            store.getters.showGenAIByFeature &&
            store.getters.getCurrentUser?.asAiActivated
        );
    });

    const userLang = computed<string>(() => store.getters.userLang || 'en');
    const currentMember = computed(() => store.getters.getCurrentUser);
    const displayNote = computed(() => store.getters.displayNote);
    const isFirstLoginSession = computed(() => store.getters.isFirstLoginSession);

    const getReleaseMessage = computed(() => {
        return release.value?.note?.message
            ? release.value.note.message[userLang.value as 'en' | 'fr']
            : t('forceUpdate.message');
    });

    const getReleaseTitle = computed(() => {
        return release.value?.title ? release.value.title[userLang.value as 'en' | 'fr'] : '';
    });
    const getReleaseInfo = async () => {
        const { data } = await $rq.getRelease({ platform: WEB });
        if (data[WEB]?.id && data[WEB].displayInNextLogin) {
            release.value = data[WEB];
        }
    };

    const handleNoteClose = async (dontShowAgain: boolean) => {
        await $rq.updateDisplayNote(currentMember.value.id, {
            displayNote: { displayNote: !dontShowAgain, platform: WEB },
        } as DisplayNoteData);
        showMessageModal.value = false;
        isReleaseNote.value = false;
    };

    const openReleaseNotes = async () => {
        await getReleaseInfo();
        isReleaseNote.value = true;
        showMessageModal.value = true;
    };

    onMounted(() => {
        store.dispatch('navigation/loadInitialNavigation');
    });
    // Watch displayNote - set by setAccessControlInfo after team selection
    watch(displayNote, async (newVal) => {
        if (!newVal || !isFirstLoginSession.value) return;
        if (currentMember.value?.displayNote[WEB]) {
            await openReleaseNotes();
        }
        store.dispatch('setFirstLoggedInSession', false);
    });

    // Watch for route changes to update sidebar expansion and content
    watch(
        () => route.path,
        (newPath) => {
            if (newPath) {
                store.dispatch('navigation/updateSidebarStateForRoute', newPath);
            }
        },
        { immediate: true },
    );
</script>
